import moment from 'moment'
import { ChartPlan } from 'src/charting/plan/chartPlan.types'
import { compileStackedBarPlan } from 'src/charting/plan/compileStackedBarPlan'
import { ChartDataRow, ChartSpec } from 'src/charting/spec/chartSpec.types'
import { ExtendCurrency } from 'src/enums'
import {
  SpendChartPoint,
  SpendChartView,
} from 'src/queries/reporting/reporting.models'
import { formatMonthDayRange } from 'src/utils/date.utils'

/**
 * Fixed-window presets shown as rows in each chart's date dropdown. The custom
 * range is handled separately via the calendar sub-view, so it is not part of
 * this union.
 */
export type ReportingPreset = 'this_month' | 'last_month'

/** A chart's active range — a fixed preset, or a user-picked custom range. */
export type ReportingRangeKind = ReportingPreset | 'custom'

export type ReportingDateRange = {
  since: moment.Moment
  until: moment.Moment
  preset: ReportingRangeKind
}

export type PanelGatedState =
  | { type: 'feature_disabled' } // Departments feature toggle is off
  | { type: 'not_entitled' } // Org is on Starter tier
  | { type: 'not_connected' } // Pro but no active ERP integration
  | { type: 'not_configured' } // Departments enabled but no teams have been created

/** Display order of the fixed presets in the dropdown. */
export const reportingPresetOrder: ReportingPreset[] = [
  'this_month',
  'last_month',
]

/** Default preset applied on load and by "Reset". */
export const DEFAULT_REPORTING_PRESET: ReportingPreset = 'this_month'

/** Localization keys for each fixed preset label (must exist in en-US.json). */
export const reportingPresetLabelKey: Record<ReportingPreset, string> = {
  this_month: 'This Month',
  last_month: 'Last Month',
}

/**
 * Resolves a fixed preset to its UTC calendar-month window (start of month to
 * end of month). UTC matches the backend's timestamp truncation. Unlike the
 * transactions date filter (which is browser-local), this is intentionally UTC.
 */
export const resolveReportingPresetWindow = (
  preset: ReportingPreset,
  now: moment.Moment = moment.utc(),
): ReportingDateRange => {
  switch (preset) {
    case 'this_month':
      return {
        since: now.clone().startOf('month'),
        until: now.clone().endOf('month'),
        preset,
      }
    case 'last_month': {
      const lastMonth = now.clone().subtract(1, 'month')
      return {
        since: lastMonth.clone().startOf('month'),
        until: lastMonth.clone().endOf('month'),
        preset,
      }
    }
  }
}

export interface ReportingPanelQueryParams {
  organizationId: string | undefined
  currency: ExtendCurrency
  enabled: boolean
  /** Shared, page-level range — every panel renders the same window. */
  dateRange: ReportingDateRange
}

export interface RankedSpendRow {
  id: string
  rank: number
  label: string
  amountCents: number
  point: SpendChartPoint
}

const otherSeriesLabel = 'Other'

export const formatDate = (date: moment.Moment): string =>
  date.format('YYYY-MM-DD')

/**
 * Human-readable date-range sub-label, e.g. "May 20 – Jun 18, 2026". Used under
 * each preset row in the dropdown and on the custom trigger.
 */
export const formatReportingDateRange = (range: ReportingDateRange): string =>
  formatMonthDayRange(range.since, range.until)

export const createDefaultDateRange = (): ReportingDateRange =>
  resolveReportingPresetWindow(DEFAULT_REPORTING_PRESET)

const parseReportingDateParam = (
  value: string | null,
): moment.Moment | undefined => {
  if (value === null) return undefined

  const parsed = moment.utc(value, 'YYYY-MM-DD', true)
  return parsed.isValid() ? parsed : undefined
}

/**
 * Supports local QA links like
 * `/reporting?fromDate=2025-03-01&toDate=2025-05-31`. When both params are
 * valid ISO dates, they become the shared custom range shown in the page-level
 * date picker and sent to every dashboard chart query.
 */
export const createDateRangeFromSearch = (
  search: string,
): ReportingDateRange | undefined => {
  const params = new URLSearchParams(search)
  const since = parseReportingDateParam(params.get('fromDate'))
  const until = parseReportingDateParam(params.get('toDate'))

  if (since === undefined || until === undefined || since.isAfter(until)) {
    return undefined
  }

  return {
    since: since.startOf('day'),
    until: until.endOf('day'),
    preset: 'custom',
  }
}

const ISO_DAY_PATTERN = /^\d{4}-\d{2}-\d{2}$/
const ISO_MONTH_PATTERN = /^\d{4}-\d{2}$/
const ISO_WEEK_PATTERN = /^\d{4}-W\d{2}$/

/**
 * Friendly x-axis label for a period bucket. The API returns machine bucket keys
 * (ISO dates); render them so the granularity follows the bucket the backend
 * chose for the range:
 * - monthly (`YYYY-MM`, wider ranges) → `MMM` (e.g. "Jun")
 * - daily (`YYYY-MM-DD`, narrow ranges) → `MMM D` (e.g. "Jun 18")
 * - weekly (`YYYY-Www`) → the week-start `MMM D`
 *
 * Anything that isn't a recognized ISO bucket (e.g. an already-human label the
 * API supplied) is returned unchanged.
 */
export const formatPeriodLabel = (raw: string): string => {
  if (!raw) return raw

  if (ISO_MONTH_PATTERN.test(raw)) {
    const month = moment.utc(raw, 'YYYY-MM', true)
    return month.isValid() ? month.format('MMM') : raw
  }

  if (ISO_DAY_PATTERN.test(raw)) {
    const day = moment.utc(raw, 'YYYY-MM-DD', true)
    return day.isValid() ? day.format('MMM D') : raw
  }

  if (ISO_WEEK_PATTERN.test(raw)) {
    const week = moment.utc(raw, 'GGGG-[W]WW', true)
    return week.isValid() ? week.format('MMM D') : raw
  }

  return raw
}

export type ReportingPeriodUnit = 'day' | 'week' | 'month'

/**
 * Infers the cadence of a set of ISO date buckets so labels can be formatted to
 * match (e.g. month-only for monthly data, even when the API keys months as the
 * first day of the month). Returns null when there are no ISO buckets.
 */
export const detectPeriodUnit = (
  isoBuckets: string[],
): ReportingPeriodUnit | null => {
  const unique = Array.from(new Set(isoBuckets)).sort()
  if (unique.length === 0) return null

  const allFirstOfMonth = unique.every(
    (bucket) => moment.utc(bucket, 'YYYY-MM-DD', true).date() === 1,
  )
  if (allFirstOfMonth) return 'month'
  if (unique.length < 2) return 'day'

  let minGapDays = Infinity
  for (let index = 1; index < unique.length; index += 1) {
    const gap = moment
      .utc(unique[index], 'YYYY-MM-DD', true)
      .diff(moment.utc(unique[index - 1], 'YYYY-MM-DD', true), 'days')
    if (gap > 0) minGapDays = Math.min(minGapDays, gap)
  }
  if (!Number.isFinite(minGapDays)) return 'day'
  if (minGapDays >= 28) return 'month'
  if (minGapDays >= 5) return 'week'
  return 'day'
}

/**
 * Unit-aware label: monthly buckets (which the API keys as the first of the
 * month, e.g. "2025-11-01") render as just the month ("Nov") rather than "Nov 1".
 * Daily/weekly fall back to {@link formatPeriodLabel} (so a daily bucket that
 * happens to be the 1st still reads "Jun 1"), as do already-human labels.
 */
export const formatPeriodLabelForUnit = (
  raw: string,
  unit: ReportingPeriodUnit,
): string => {
  if (unit === 'month' && ISO_DAY_PATTERN.test(raw)) {
    const month = moment.utc(raw, 'YYYY-MM-DD', true)
    if (month.isValid()) return month.format('MMM')
  }
  return formatPeriodLabel(raw)
}

export const isOpaqueReportingKey = (label: string | undefined): boolean => {
  if (label === undefined) return true
  const value = label.trim()
  if (value === '') return true

  return (
    /^(dept|department|team|user|person|card|cardholder|vc|vcn|usr)_[a-z0-9_-]+$/i.test(
      value,
    ) ||
    /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(
      value,
    )
  )
}

export const firstPresentMetadataValue = (
  point: SpendChartPoint,
  keys: string[],
): string | undefined => {
  for (const key of keys) {
    const value = point.metadata[key]
    if (value !== undefined && value !== null && value.trim() !== '') {
      return value
    }
  }

  return undefined
}

export const getPointDisplayLabel = (
  point: SpendChartPoint,
  fallbackLabel: string,
  metadataKeys: string[] = [],
): string => {
  const metadataLabel = firstPresentMetadataValue(point, metadataKeys)
  if (metadataLabel !== undefined && !isOpaqueReportingKey(metadataLabel)) {
    return metadataLabel
  }

  const label = point.seriesLabel ?? point.label
  if (!isOpaqueReportingKey(label)) return label

  return fallbackLabel
}

export const formatMoney = (
  amountCents: number,
  currency: ExtendCurrency,
  locale = 'en-US',
): string =>
  new Intl.NumberFormat(locale, {
    style: 'currency',
    currency,
    maximumFractionDigits: 2,
  }).format(amountCents / 100)

export const formatCompactMoney = (
  amountCents: number,
  currency: ExtendCurrency,
  locale = 'en-US',
): string =>
  new Intl.NumberFormat(locale, {
    style: 'currency',
    currency,
    notation: 'compact',
    maximumFractionDigits: 1,
  }).format(amountCents / 100)

export const formatPercentage = (value: number, locale = 'en-US'): string =>
  new Intl.NumberFormat(locale, {
    style: 'percent',
    maximumFractionDigits: 0,
  }).format(value)

export const getTopSeriesStat = (
  plan: ChartPlan | undefined,
  unknownLabel?: string,
): { label: string; amountCents: number } | undefined => {
  if (!plan) return undefined

  const totalsBySeries = plan.categories
    .flatMap((category) => category.segments)
    .reduce<Map<string, number>>((acc, segment) => {
      acc.set(
        segment.seriesLabel,
        (acc.get(segment.seriesLabel) ?? 0) + segment.value,
      )
      return acc
    }, new Map())

  const getLargestSeries = (excluded: ReadonlySet<string>) =>
    Array.from(totalsBySeries.entries()).reduce<
      { label: string; amountCents: number } | undefined
    >((top, [label, amountCents]) => {
      if (excluded.has(label)) return top
      if (!top || amountCents > top.amountCents) return { label, amountCents }
      return top
    }, undefined)

  // Never headline the synthetic "Unknown"/uncategorized bucket as the top
  // series. Prefer a real named series; fall back to "Other" if that's all
  // there is — but never the Unknown bucket (return undefined instead, so the
  // panel shows total spend rather than "Top X: Unknown").
  const unknownExcluded = new Set<string>(unknownLabel ? [unknownLabel] : [])
  const otherAndUnknown = new Set<string>([
    otherSeriesLabel,
    ...unknownExcluded,
  ])

  return getLargestSeries(otherAndUnknown) ?? getLargestSeries(unknownExcluded)
}

export const getInitials = (label: string): string => {
  const words = label.trim().split(/\s+/).filter(Boolean)

  if (words.length >= 2) {
    return `${words[0][0]}${words[1][0]}`.toUpperCase()
  }

  return label.slice(0, 2).toUpperCase()
}

export const hasSpendData = (chart: SpendChartView | undefined): boolean =>
  (chart?.points ?? []).some((point) => point.spendCents !== 0)

/**
 * The full, ordered list of period bucket keys spanning the chart's selected
 * range, so the x-axis shows every period (including empty ones) instead of only
 * the buckets that happen to have spend. The API omits empty periods, which
 * otherwise makes a wide range look like it "snaps" to just the months with data.
 *
 * Returns undefined (no gap-filling, current behavior) when the buckets aren't
 * plain ISO dates or the API supplied its own human period labels — in those
 * cases we can't safely synthesize the missing bucket keys.
 */
export const buildPeriodDomain = (
  chart: SpendChartView,
): string[] | undefined => {
  const hasExplicitLabels = chart.points.some(
    (point) =>
      firstPresentMetadataValue(point, ['bucketLabel', 'periodLabel']) !==
      undefined,
  )
  if (hasExplicitLabels) return undefined

  const isoBuckets = chart.points
    .map((point) => point.bucket)
    .filter(
      (bucket): bucket is string =>
        typeof bucket === 'string' && ISO_DAY_PATTERN.test(bucket),
    )
  if (isoBuckets.length === 0) return undefined

  const since = moment.utc(chart.since, 'YYYY-MM-DD', true)
  const until = moment.utc(chart.until, 'YYYY-MM-DD', true)
  if (!since.isValid() || !until.isValid() || until.isBefore(since)) {
    return undefined
  }

  const unique = Array.from(new Set(isoBuckets)).sort()
  const anchor = moment.utc(unique[0], 'YYYY-MM-DD', true)
  const unit: moment.unitOfTime.DurationConstructor =
    detectPeriodUnit(isoBuckets) ?? 'day'

  // Walk the anchor back in whole steps so generated keys keep the data's phase
  // (day-of-month for monthly, weekday for weekly), then forward to cover [since, until].
  const rangeStart = unit === 'month' ? since.clone().startOf('month') : since
  const cursor = anchor.clone()
  let guard = 0
  while (cursor.isAfter(rangeStart) && guard < 5000) {
    cursor.subtract(1, unit)
    guard += 1
  }

  const domain: string[] = []
  guard = 0
  while (cursor.isSameOrBefore(until) && guard < 5000) {
    domain.push(cursor.format('YYYY-MM-DD'))
    cursor.add(1, unit)
    guard += 1
  }

  return domain.length > 0 ? domain : undefined
}

/** Hard cap on the number of bars per chart (docs: ~8 is the readable density). */
export const MAX_CHART_COLUMNS = 8

/** Human label for a column that groups several contiguous period buckets. */
const formatPeriodRange = (members: string[]): string => {
  if (members.length <= 1) return formatPeriodLabel(members[0] ?? '')

  const start = moment.utc(members[0], 'YYYY-MM-DD', true)
  const end = moment.utc(members[members.length - 1], 'YYYY-MM-DD', true)
  if (!start.isValid() || !end.isValid()) return formatPeriodLabel(members[0])

  const monthly = members.every(
    (member) => moment.utc(member, 'YYYY-MM-DD', true).date() === 1,
  )
  if (monthly) {
    return start.isSame(end, 'month')
      ? start.format('MMM')
      : `${start.format('MMM')} – ${end.format('MMM')}`
  }
  return start.isSame(end, 'month')
    ? `${start.format('MMM D')} – ${end.format('D')}`
    : `${start.format('MMM D')} – ${end.format('MMM D')}`
}

/**
 * Caps the period axis at {@link MAX_CHART_COLUMNS} by grouping the (gap-filled)
 * domain into equal contiguous chunks — spend is summed per chunk, so totals
 * reconcile. Returns the display domain plus a mapper from a raw bucket key to
 * its column key. A domain already within the cap is returned unchanged.
 */
export const capPeriodColumns = (
  domain: string[],
): { domain: string[]; columnForBucket: (bucket: string) => string } => {
  if (domain.length <= MAX_CHART_COLUMNS) {
    return { domain, columnForBucket: (bucket) => bucket }
  }

  const size = Math.ceil(domain.length / MAX_CHART_COLUMNS)
  const columns: string[] = []
  const columnByBucket = new Map<string, string>()

  for (let start = 0; start < domain.length; start += size) {
    const members = domain.slice(start, start + size)
    const label = formatPeriodRange(members)
    columns.push(label)
    members.forEach((member) => columnByBucket.set(member, label))
  }

  return {
    domain: columns,
    columnForBucket: (bucket) => columnByBucket.get(bucket) ?? bucket,
  }
}

export const toStackedBarPlan = ({
  chart,
  title,
  description,
  ariaLabel,
  unknownLabel,
  seriesLabelForPoint,
}: {
  chart: SpendChartView
  title: string
  description: string
  ariaLabel: string
  unknownLabel: string
  seriesLabelForPoint?: (point: SpendChartPoint) => string
}): ChartPlan => {
  // Gap-fill the period axis to the selected range, then cap it at
  // MAX_CHART_COLUMNS by grouping contiguous buckets (spend is summed per group).
  const gapFilledDomain = buildPeriodDomain(chart)
  const { domain: categoryDomain, columnForBucket } = gapFilledDomain
    ? capPeriodColumns(gapFilledDomain)
    : { domain: undefined, columnForBucket: (bucket: string) => bucket }

  // Cadence of the buckets, used to format axis labels (month-only for monthly
  // data). Only meaningful when the buckets are plain ISO dates (gap-fill ran).
  const periodUnit = gapFilledDomain
    ? detectPeriodUnit(
        chart.points
          .map((point) => point.bucket)
          .filter(
            (bucket): bucket is string =>
              typeof bucket === 'string' && ISO_DAY_PATTERN.test(bucket),
          ),
      )
    : null

  const rawRows: ChartDataRow[] = chart.points.map((point) => {
    const periodLabel =
      firstPresentMetadataValue(point, ['bucketLabel', 'periodLabel']) ??
      point.bucket ??
      point.label
    const seriesLabel =
      seriesLabelForPoint?.(point) ??
      getPointDisplayLabel(point, unknownLabel, [
        'seriesLabel',
        'label',
        'name',
        'displayName',
      ])

    return {
      period: columnForBucket(periodLabel),
      series: seriesLabel,
      spendCents: point.spendCents,
      bucket: point.bucket,
    }
  })

  const topSeriesLabels = Array.from(
    rawRows
      .reduce<Map<string, number>>((acc, row) => {
        const label = String(row.series ?? unknownLabel)
        acc.set(label, (acc.get(label) ?? 0) + Number(row.spendCents ?? 0))
        return acc
      }, new Map())
      .entries(),
  )
    .filter(([label]) => label !== otherSeriesLabel)
    .sort(([, a], [, b]) => b - a)
    .slice(0, 6)
    .map(([label]) => label)

  const topSeriesLabelSet = new Set(topSeriesLabels)
  const rewrittenRows = rawRows.map((row) => {
    const label = String(row.series ?? unknownLabel)
    if (label === otherSeriesLabel || topSeriesLabelSet.has(label)) return row

    return {
      ...row,
      series: otherSeriesLabel,
    }
  })
  const rows = Array.from(
    rewrittenRows
      .reduce<Map<string, ChartDataRow>>((acc, row) => {
        const period = String(row.period ?? '')
        const series = String(row.series ?? unknownLabel)
        const key = JSON.stringify([period, series])
        const current = acc.get(key)

        if (!current) {
          acc.set(key, row)
          return acc
        }

        acc.set(key, {
          ...current,
          spendCents:
            Number(current.spendCents ?? 0) + Number(row.spendCents ?? 0),
        })
        return acc
      }, new Map())
      .values(),
  )

  const seriesDomain = Array.from(
    new Set(rows.map((row) => String(row.series ?? unknownLabel))),
  )

  const spec: ChartSpec = {
    version: 1,
    mark: 'bar',
    title,
    description,
    ariaLabel,
    encoding: {
      x: {
        field: 'period',
        type: 'ordinal',
        title: 'Period',
        ...(categoryDomain ? { domain: categoryDomain } : {}),
      },
      y: {
        field: 'spendCents',
        type: 'quantitative',
        stack: 'diverging',
        valueFormat: 'currencyCents',
        title: 'Spend',
      },
      color: {
        field: 'series',
        type: 'nominal',
        domain: seriesDomain,
      },
    },
  }

  const plan = compileStackedBarPlan(spec, rows)
  if (!periodUnit) return plan

  // Format the (raw ISO) category labels for the detected cadence — e.g. monthly
  // buckets keyed "2025-11-01" display as "Nov", not "Nov 1". Grouped columns
  // already carry human range labels and pass through unchanged. Keys stay raw.
  return {
    ...plan,
    categories: plan.categories.map((category) => ({
      ...category,
      label: formatPeriodLabelForUnit(category.key, periodUnit),
    })),
  }
}
